import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Settings, Activity, AlertTriangle, CheckCircle, XCircle, RefreshCw, Download, Upload, Zap } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import PadaidLogo from '../../../components/ui/PadaidLogo';
import { useAuth } from '../../../contexts/AuthContext';
import { adminGetMaintenance, adminSaveMaintenance } from '../../../services/site';

const SystemAdmin = () => {
  const { token, user } = useAuth();
  const [systemStatus, setSystemStatus] = useState({
    website: 'online',
    database: 'optimal',
    mobileApp: 'active',
    payments: 'functional',
    email: 'maintenance',
    backups: 'updated'
  });

  const [systemMetrics, setSystemMetrics] = useState({
    cpuUsage: 45,
    memoryUsage: 62,
    diskUsage: 38,
    networkTraffic: 75
  });

  const [logs, setLogs] = useState([
    { id: 1, time: '2024-01-15 14:30:25', level: 'INFO', message: 'User login successful', component: 'AUTH' },
  ]);

  const [settings, setSettings] = useState({
    maintenanceMode: false,
    registrationEnabled: true,
    emailNotifications: true,
    autoBackup: true,
    secureMode: true,
    debugMode: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Charger l'état maintenance réel
    const load = async () => {
      try {
        setLoading(true);
        const maint = await adminGetMaintenance(token);
        setSettings((prev) => ({ ...prev, maintenanceMode: !!maint.enabled }));
      } catch (e) {
        // ignorer en cas d'accès non fondateur
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSystemAction = async (action) => {
    switch (action) {
      case 'restart':
        if (window.confirm('Êtes-vous sûr de vouloir redémarrer le système ?')) {
          console.log('System restart initiated');
        }
        break;
      case 'backup':
        console.log('Manual backup initiated');
        break;
      case 'maintenance': {
        try {
          const next = !settings.maintenanceMode;
          setLoading(true);
          setError(''); setSuccess('');
          const res = await adminSaveMaintenance({ enabled: next, message: next ? 'Site en maintenance, merci de revenir bientôt.' : null }, token);
          setSettings((prev) => ({ ...prev, maintenanceMode: !!res.enabled }));
          setSuccess(next ? '✅ Maintenance activée' : '✅ Maintenance désactivée');
        } catch (e) {
          setError(e.response?.data?.detail || e.message);
        } finally {
          setLoading(false);
        }
        break;
      }
      default:
        break;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
      case 'optimal':
      case 'active':
      case 'functional':
      case 'updated':
        return 'text-green-500';
      case 'maintenance':
      case 'warning':
        return 'text-yellow-500';
      case 'offline':
      case 'error':
      case 'critical':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
      case 'optimal':
      case 'active':
      case 'functional':
      case 'updated':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'maintenance':
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'offline':
      case 'error':
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <RefreshCw className="h-4 w-4 text-gray-500" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'INFO':
        return 'text-blue-500';
      case 'WARNING':
        return 'text-yellow-500';
      case 'ERROR':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-destructive/5">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Button variant="ghost" size="sm" asChild className="mr-4">
                <Link to="/dashboard/fondateur">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour Dashboard
                </Link>
              </Button>
              <PadaidLogo size="md" showText={true} />
              <div className="ml-4">
                <h1 className="text-lg font-semibold text-destructive">Administration Système</h1>
                <p className="text-sm text-muted-foreground">Contrôle technique complet de PADAID</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant={settings.maintenanceMode ? "destructive" : "secondary"}>
                {settings.maintenanceMode ? "Mode Maintenance" : "Système Actif"}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && <div className="mb-4 text-sm text-red-500">{error}</div>}
        {success && <div className="mb-4 text-sm text-green-600">{success}</div>}

        {/* Maintenance Mode Alert */}
        {settings.maintenanceMode && (
          <Alert className="mb-8 border-destructive/20 bg-destructive/5">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Le mode maintenance est activé. Le site n'est accessible qu'aux administrateurs.
            </AlertDescription>
          </Alert>
        )}

        {/* System Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Statut Système</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(systemStatus).map(([service, status]) => (
                  <div key={service} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{service.replace(/([A-Z])/g, ' $1')}</span>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(status)}
                      <span className={`text-xs capitalize ${getStatusColor(status)}`}>
                        {status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Performances</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm">
                    <span>CPU</span>
                    <span>{systemMetrics.cpuUsage}%</span>
                  </div>
                  <Progress value={systemMetrics.cpuUsage} className="h-2 mt-1" />
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Mémoire</span>
                    <span>{systemMetrics.memoryUsage}%</span>
                  </div>
                  <Progress value={systemMetrics.memoryUsage} className="h-2 mt-1" />
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Disque</span>
                    <span>{systemMetrics.diskUsage}%</span>
                  </div>
                  <Progress value={systemMetrics.diskUsage} className="h-2 mt-1" />
                </div>
                <div>
                  <div className="flex justify-between text-sm">
                    <span>Réseau</span>
                    <span>{systemMetrics.networkTraffic}%</span>
                  </div>
                  <Progress value={systemMetrics.networkTraffic} className="h-2 mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Actions Rapides</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => handleSystemAction('backup')}
                disabled={loading}
              >
                <Database className="mr-2 h-4 w-4" />
                Sauvegarde Manuelle
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => handleSystemAction('maintenance')}
                disabled={loading}
              >
                <Shield className="mr-2 h-4 w-4" />
                {settings.maintenanceMode ? 'Désactiver' : 'Activer'} Maintenance
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="destructive"
                onClick={() => handleSystemAction('restart')}
                disabled={loading}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Redémarrer Système
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Management */}
        <Tabs defaultValue="logs" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="logs">Journaux</TabsTrigger>
            <TabsTrigger value="settings">Paramètres</TabsTrigger>
            <TabsTrigger value="database">Base de Données</TabsTrigger>
            <TabsTrigger value="security">Sécurité</TabsTrigger>
          </TabsList>

          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="mr-2 h-5 w-5" />
                  Journaux Système
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {logs.map((log) => (
                    <div key={log.id} className="flex items-start space-x-4 p-3 border rounded">
                      <span className="text-xs text-muted-foreground font-mono">
                        {log.time}
                      </span>
                      <Badge variant="outline" className={getLevelColor(log.level)}>
                        {log.level}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        [{log.component}]
                      </span>
                      <span className="text-sm flex-1">
                        {log.message}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-4">
                  <Button variant="outline">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Actualiser
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Exporter Logs
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="mr-2 h-5 w-5" />
                  Paramètres Système
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="maintenance">Mode Maintenance</Label>
                        <p className="text-sm text-muted-foreground">
                          Désactiver temporairement le site
                        </p>
                      </div>
                      <Switch
                        id="maintenance"
                        checked={settings.maintenanceMode}
                        onCheckedChange={(checked) => handleSettingChange('maintenanceMode', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="registration">Inscriptions</Label>
                        <p className="text-sm text-muted-foreground">
                          Permettre les nouvelles inscriptions
                        </p>
                      </div>
                      <Switch
                        id="registration"
                        checked={settings.registrationEnabled}
                        onCheckedChange={(checked) => handleSettingChange('registrationEnabled', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="emails">Notifications Email</Label>
                        <p className="text-sm text-muted-foreground">
                          Envoyer les notifications par email
                        </p>
                      </div>
                      <Switch
                        id="emails"
                        checked={settings.emailNotifications}
                        onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="backup">Sauvegarde Auto</Label>
                        <p className="text-sm text-muted-foreground">
                          Sauvegardes automatiques quotidiennes
                        </p>
                      </div>
                      <Switch
                        id="backup"
                        checked={settings.autoBackup}
                        onCheckedChange={(checked) => handleSettingChange('autoBackup', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="secure">Mode Sécurisé</Label>
                        <p className="text-sm text-muted-foreground">
                          Sécurité renforcée et 2FA obligatoire
                        </p>
                      </div>
                      <Switch
                        id="secure"
                        checked={settings.secureMode}
                        onCheckedChange={(checked) => handleSettingChange('secureMode', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="debug">Mode Debug</Label>
                        <p className="text-sm text-muted-foreground">
                          Logs détaillés pour développement
                        </p>
                      </div>
                      <Switch
                        id="debug"
                        checked={settings.debugMode}
                        onCheckedChange={(checked) => handleSettingChange('debugMode', checked)}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <Button className="bg-primary hover:bg-primary/90" onClick={() => handleSystemAction('maintenance')} disabled={loading}>
                    {settings.maintenanceMode ? 'Désactiver Maintenance' : 'Activer Maintenance'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="database">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="mr-2 h-5 w-5" />
                  Gestion Base de Données
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-medium">Statistiques</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Taille totale:</span>
                        <span>245 MB</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Nombre de tables:</span>
                        <span>12</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Connexions actives:</span>
                        <span>8</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Dernière sauvegarde:</span>
                        <span>Il y a 2h</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-medium">Actions</h3>
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full justify-start">
                        <Download className="mr-2 h-4 w-4" />
                        Exporter Base
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <Upload className="mr-2 h-4 w-4" />
                        Importer Base
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Optimiser Tables
                      </Button>
                      <Button variant="destructive" className="w-full justify-start">
                        <AlertTriangle className="mr-2 h-4 w-4" />
                        Réinitialiser
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Shield className="mr-2 h-5 w-5" />
                  Sécurité Système
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Système sécurisé - Dernière analyse: il y a 1 heure - Aucune menace détectée
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SystemAdmin;